import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
    House, Receipt, ChartLineUp, Gear, SignOut, Package, Globe, MagnifyingGlass, List, X, Envelope, Storefront, GoogleLogo, Truck, Wrench, FileText
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";

const navItems = [
    { to: "/", label: "Tableau de bord", icon: House, end: true, testid: "nav-dashboard" },
    { to: "/factures", label: "Factures", icon: Receipt, testid: "nav-invoices" },
    { to: "/bons-livraison", label: "Bons de livraison", icon: Truck, testid: "nav-bons-livraison" },
    { to: "/bons-intervention", label: "Bons d'intervention", icon: Wrench, testid: "nav-bons-intervention" },
    { to: "/devis-acceptes", label: "Devis acceptés", icon: FileText, testid: "nav-devis-acceptes" },
    { to: "/recherche", label: "Recherche", icon: MagnifyingGlass, testid: "nav-search" },
    { to: "/fournisseurs", label: "Fournisseurs", icon: Storefront, testid: "nav-suppliers" },
    { to: "/connecteur-mail", label: "Connecteur mail", icon: Envelope, testid: "nav-mail" },
    { to: "/scanner", label: "Scanner Drive", icon: GoogleLogo, testid: "nav-scanner" },
    { to: "/rapports", label: "Rapports", icon: ChartLineUp, testid: "nav-reports" },
    { to: "/parametres", label: "Paramètres", icon: Gear, testid: "nav-settings" },
];

function NavLinks({ onNavigate }) {
    return (
        <nav className="flex-1 p-3 space-y-1">
            {navItems.map(({ to, label, icon: Icon, end, testid }) => (
                <NavLink
                    key={to}
                    to={to}
                    end={end}
                    data-testid={testid}
                    className="block"
                    onClick={onNavigate}
                >
                    {({ isActive }) => (
                        <span
                            className={`sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                isActive
                                    ? "bg-accent text-foreground font-semibold"
                                    : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                            }`}
                            data-active={isActive ? "true" : "false"}
                        >
                            <Icon weight={isActive ? "fill" : "regular"} size={18} />
                            {label}
                        </span>
                    )}
                </NavLink>
            ))}
        </nav>
    );
}

function Brand() {
    return (
        <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                <Package weight="duotone" size={18} />
            </div>
            <div>
                <div className="font-display font-bold leading-none">ScanBox</div>
                <div className="overline mt-1">Ged Simplifiée</div>
            </div>
        </div>
    );
}

function UserFooter({ user, onLogout }) {
    return (
        <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
                <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-secondary text-secondary-foreground text-xs font-semibold">
                        {(user?.name || user?.email || "?").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{user?.name || "Utilisateur"}</div>
                    <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                </div>
            </div>
            <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-muted-foreground hover:text-foreground"
                onClick={onLogout}
                data-testid="logout-btn"
            >
                <SignOut size={16} className="mr-2" />
                Déconnexion
            </Button>
        </div>
    );
}

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen flex bg-background text-foreground">
            {/* Desktop sidebar */}
            <aside className="hidden md:flex w-60 border-r border-border bg-card flex-col" data-testid="sidebar">
                <div className="px-6 py-7 flex items-center gap-2 border-b border-border">
                    <Brand />
                </div>
                <NavLinks />
                <UserFooter user={user} onLogout={handleLogout} />
            </aside>

            <main className="flex-1 min-w-0">
                {/* Mobile top bar */}
                <header className="md:hidden sticky top-0 z-30 flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-card/95 backdrop-blur">
                    <Brand />
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Ouvrir le menu"
                                data-testid="mobile-menu-btn"
                            >
                                <List size={22} />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-72 p-0 flex flex-col">
                            <SheetHeader className="px-6 py-5 border-b border-border">
                                <SheetTitle className="text-left flex items-center gap-2">
                                    <Brand />
                                </SheetTitle>
                            </SheetHeader>
                            <NavLinks onNavigate={() => setMobileOpen(false)} />
                            <UserFooter
                                user={user}
                                onLogout={() => { setMobileOpen(false); handleLogout(); }}
                            />
                        </SheetContent>
                    </Sheet>
                </header>

                <Outlet />
            </main>
        </div>
    );
}
